import { useRef, useState, type FormEvent } from "react";

const SUBMIT_ADDR = "https://readdy.ai/api/form/dah9aajoh653ivfvnp20";
const FORM_ID = "dah9aajoh653ivfvnp20";

type Status = "idle" | "submitting" | "success" | "error";

const submitTypes = ["提供线索", "报告纠错", "内容合作", "其他咨询"];
const domains = ["社会民生", "科技前沿", "文娱动态", "健康生活", "体育赛场", "其他领域"];

export default function ContactForm() {
  const [status, setStatus] = useState<Status>("idle");
  const [errorMsg, setErrorMsg] = useState("");
  const [messageLength, setMessageLength] = useState(0);
  const formRef = useRef<HTMLFormElement>(null);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (status === "submitting") return;

    const formEl = event.currentTarget;
    const formData = new FormData(formEl);

    const guard = String(formData.get("website_alt") ?? "").trim();
    if (guard) {
      setStatus("success");
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      const text = String(value).trim();
      if (!text) return;
      if (key === "website_alt") return;
      payload.append(key, text);
    });

    setStatus("submitting");
    setErrorMsg("");

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");
      const succeeded = response.ok && parsed?.code === "OK" && !isSpam;

      if (succeeded) {
        setStatus("success");
        formRef.current?.reset();
        setMessageLength(0);
        return;
      }

      setStatus("error");
      setErrorMsg(
        isSpam
          ? "提交未通过校验，请检查填写内容后重试。"
          : serverMsg || "提交失败，请稍后重试或直接发送邮件联系我们。",
      );
    } catch {
      setStatus("error");
      setErrorMsg("网络连接异常，提交未成功。请稍后重试，或直接发送邮件联系我们。");
    }
  };

  if (status === "success") {
    return (
      <div className="flex flex-col items-center rounded-lg border border-secondary-200 bg-secondary-50 px-6 py-12 text-center">
        <span className="flex h-14 w-14 items-center justify-center rounded-full bg-secondary-500 text-background-50">
          <i className="ri-check-line text-2xl" aria-hidden="true"></i>
        </span>
        <h3 className="mt-4 font-heading text-lg font-bold text-secondary-900">线索已收到，谢谢你</h3>
        <p className="mt-2 max-w-md text-sm leading-relaxed text-secondary-800">
          编辑会在两个工作日内评估材料是否足以立档。如果材料需要补充，我们会通过你留下的方式联系你。
        </p>
        <button
          type="button"
          onClick={() => setStatus("idle")}
          className="mt-6 inline-flex items-center gap-2 rounded-md border border-secondary-300 px-5 py-2.5 text-sm text-secondary-900 transition-colors hover:bg-secondary-100 whitespace-nowrap"
        >
          <i className="ri-edit-2-line" aria-hidden="true"></i>
          再提交一条
        </button>
      </div>
    );
  }

  return (
    <form
      ref={formRef}
      id={FORM_ID}
      data-readdy-form
      onSubmit={handleSubmit}
      className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-7"
    >
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        <div>
          <label htmlFor="contact-name" className="mb-2 block text-sm font-medium text-foreground-900">
            称呼
          </label>
          <input
            id="contact-name"
            name="name"
            type="text"
            required
            placeholder="怎么称呼你"
            className="h-11 w-full rounded-md border border-background-300 bg-background-100 px-3 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:bg-background-50"
          />
        </div>
        <div>
          <label htmlFor="contact-email" className="mb-2 block text-sm font-medium text-foreground-900">
            联系邮箱
          </label>
          <input
            id="contact-email"
            name="email"
            type="email"
            required
            placeholder="name@example.com"
            className="h-11 w-full rounded-md border border-background-300 bg-background-100 px-3 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:bg-background-50"
          />
        </div>
        <div>
          <label htmlFor="contact-type" className="mb-2 block text-sm font-medium text-foreground-900">
            提交类型
          </label>
          <div className="relative">
            <select
              id="contact-type"
              name="type"
              defaultValue={submitTypes[0]}
              className="h-11 w-full appearance-none rounded-md border border-background-300 bg-background-100 pl-3 pr-9 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400"
            >
              {submitTypes.map((item) => (
                <option key={item} value={item}>
                  {item}
                </option>
              ))}
            </select>
            <span className="pointer-events-none absolute right-3 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center text-foreground-400">
              <i className="ri-arrow-down-s-line text-base" aria-hidden="true"></i>
            </span>
          </div>
        </div>
        <div>
          <label htmlFor="contact-domain" className="mb-2 block text-sm font-medium text-foreground-900">
            涉及领域
          </label>
          <div className="relative">
            <select
              id="contact-domain"
              name="domain"
              defaultValue={domains[0]}
              className="h-11 w-full appearance-none rounded-md border border-background-300 bg-background-100 pl-3 pr-9 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400"
            >
              {domains.map((item) => (
                <option key={item} value={item}>
                  {item}
                </option>
              ))}
            </select>
            <span className="pointer-events-none absolute right-3 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center text-foreground-400">
              <i className="ri-arrow-down-s-line text-base" aria-hidden="true"></i>
            </span>
          </div>
        </div>
      </div>

      <div className="mt-5">
        <div className="mb-2 flex items-center justify-between">
          <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-900">
            线索或问题描述
          </label>
          <span className="font-label text-[11px] text-foreground-400">{messageLength}/500</span>
        </div>
        <textarea
          id="contact-message"
          name="message"
          required
          rows={6}
          maxLength={500}
          onChange={(event) => setMessageLength(event.target.value.length)}
          placeholder="请尽量写明事件时间、涉及的说法，以及你手上有哪些材料（截图、录音、单据等）。涉及隐私的部分可以不写细节。"
          className="w-full rounded-md border border-background-300 bg-background-100 px-3 py-2.5 text-sm leading-relaxed text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:bg-background-50"
        ></textarea>
      </div>

      <div className="aux-field-holder" aria-hidden="true">
        <label htmlFor="contact-website-alt">Website</label>
        <input
          id="contact-website-alt"
          name="website_alt"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          readOnly
        />
      </div>

      <div className="mt-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <p className="flex items-start gap-2 text-xs leading-relaxed text-foreground-500">
          <i className="ri-shield-check-line mt-0.5 text-secondary-500" aria-hidden="true"></i>
          我们会对投稿人身份保密，材料仅用于求证，不做二次交易。
        </p>
        <button
          type="submit"
          disabled={status === "submitting"}
          className="inline-flex items-center justify-center gap-2 rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 whitespace-nowrap"
        >
          <i className={status === "submitting" ? "ri-loader-4-line animate-spin" : "ri-send-plane-line"} aria-hidden="true"></i>
          {status === "submitting" ? "提交中…" : "提交线索"}
        </button>
      </div>

      {status === "error" && errorMsg && (
        <p role="alert" className="mt-4 flex items-start gap-2 rounded-md border border-primary-200 bg-primary-50 px-4 py-3 text-sm text-primary-800">
          <i className="ri-error-warning-line mt-0.5" aria-hidden="true"></i>
          {errorMsg}
        </p>
      )}
    </form>
  );
}